import React, { useState, useCallback, useMemo } from 'react'
import { Formik, Form, Field, FieldProps } from 'formik'
import dayjs from 'dayjs'
import {
  Container,
  Box,
  Center,
  Flex,
  FormControl,
  Checkbox,
  Button,
  IconButton,
  Text,
} from '@chakra-ui/react'
import { AddIcon, MinusIcon } from '@chakra-ui/icons'

import { Enquete, EnqueteStats, DisplayUnit } from 'types/generated/graphql'
import {
  EnqueteFormSchema,
  EnqueteFormValues,
} from 'services/validators/EnqueteFormSchema'
import { FormInput } from 'components/Common/InputPageComponent/FormInput'
import { FormTextarea } from 'components/Common/InputPageComponent/FormTextarea'
import { EnqueteImageForm } from './EnqueteImageForm'
import { FormDateTime } from '../Common/InputPageComponent/FormDateTime'
import { FormRadio } from '../Common/InputPageComponent/FormRadio'

type Props = {
  initialValues?: Enquete
  enqueteStats?: EnqueteStats
  submitButtonLabel: string
  onSubmit: (values: EnqueteFormValues) => void
}

export const EnqueteForm: React.FC<Props> = ({
  initialValues,
  enqueteStats,
  submitButtonLabel,
  onSubmit,
}) => {
  const getStatLabel = (index: number) => {
    if (!enqueteStats?.stats[index]) {
      return ''
    }
    return `　${enqueteStats?.stats[index].count}票(${enqueteStats?.stats[index].percent})`
  }
  const [optionsCsv, setOptionsCsv] = useState(() => {
    if (!initialValues) {
      return [
        { name: 'optionsCsv.0', label: '選択肢1', placeholder: '選択肢1' },
        { name: 'optionsCsv.1', label: '選択肢2', placeholder: '選択肢2' },
      ]
    }

    return initialValues.optionsCsv.split(',').map((_, i) => ({
      name: `optionsCsv.${i}`,
      label: `選択肢${i + 1}${getStatLabel(i)}`,
      placeholder: `選択肢${i + 1}`,
    }))
  })

  const delOptionsCsv = useCallback(
    (values: EnqueteFormValues) => {
      optionsCsv.pop()
      setOptionsCsv([...optionsCsv])
      values.optionsCsv.pop()
    },
    [optionsCsv, setOptionsCsv]
  )

  const addOptionsCsv = useCallback(
    (values: EnqueteFormValues) => {
      setOptionsCsv([
        ...optionsCsv,
        {
          name: `optionsCsv.${optionsCsv.length}`,
          label: `選択肢${optionsCsv.length + 1}`,
          placeholder: `選択肢${optionsCsv.length + 1}`,
        },
      ])
      values.optionsCsv.push('')
    },
    [optionsCsv, setOptionsCsv]
  )

  const content = useMemo<EnqueteFormValues>(() => {
    if (!initialValues) {
      return EnqueteFormSchema.getDefault()
    }

    return {
      title: initialValues.title,
      question: initialValues.question,
      deadline: dayjs(initialValues.deadline).format('YYYY-MM-DD HH:mm'),
      optionsCsv: initialValues.optionsCsv.split(','),
      published: initialValues.published,
      imageUrl: initialValues.imageUrl ?? '',
      displayUnit: initialValues.displayUnit,
    }
  }, [initialValues])

  return (
    <Formik
      initialValues={EnqueteFormSchema.cast(content, {
        stripUnknown: true,
      })}
      validationSchema={EnqueteFormSchema}
      onSubmit={onSubmit}
      validateOnBlur
    >
      {({ values, errors, touched, isSubmitting, isValid }) => {
        return (
          <Form>
            <Container maxW="container.md">
              <Flex direction="column">
                <EnqueteImageForm />
                <Box mb="1rem">
                  <FormInput name="title" label="タイトル" isRequired />
                  <FormTextarea name="question" label="質問文" isRequired />
                  <FormDateTime name="deadline" label="回答期限" />
                  <FormRadio
                    name="displayUnit"
                    label="結果画面で表示する単位"
                    values={[DisplayUnit.Percentage, DisplayUnit.Vote]}
                    radioBtnLabels={['％', '票数']}
                    isRequired
                  />
                </Box>
                <Box mb="1rem">
                  {enqueteStats && (
                    <Text fontSize="sm" mb="1rem">
                      総投票数:{enqueteStats.total}件
                    </Text>
                  )}
                  {optionsCsv.map((option) => (
                    <FormInput
                      className="optionCsv"
                      key={option.name}
                      name={option.name}
                      label={option.label}
                      placeholder={option.placeholder}
                      isRequired
                    />
                  ))}
                  {touched.optionsCsv &&
                    errors.optionsCsv &&
                    errors.optionsCsv != '選択肢は2つ以上必要です' && (
                      <Text fontSize="sm" color="#E53E3E" pb={2}>
                        {errors.optionsCsv}
                      </Text>
                    )}
                  <Center>
                    {optionsCsv.length < 10 && (
                      <IconButton
                        colorScheme="red"
                        aria-label="選択肢欄追加"
                        isRound
                        icon={<AddIcon />}
                        onClick={() => addOptionsCsv(values)}
                        mr={2}
                      />
                    )}
                    {optionsCsv.length > 2 && (
                      <IconButton
                        colorScheme="blue"
                        aria-label="選択肢欄削除"
                        isRound
                        icon={<MinusIcon />}
                        onClick={() => delOptionsCsv(values)}
                      />
                    )}
                  </Center>
                </Box>
                <Field name="published">
                  {({ form }: FieldProps) => (
                    <FormControl
                      name="published"
                      display="flex"
                      alignItems="center"
                      mb={4}
                    >
                      <Checkbox
                        name="published"
                        id="published"
                        colorScheme="red"
                        defaultChecked={values.published}
                        onChange={() => {
                          form.setFieldValue('published', !values.published)
                        }}
                      >
                        公開する
                      </Checkbox>
                    </FormControl>
                  )}
                </Field>
                <Button
                  type="submit"
                  m="auto"
                  bgColor="#F3B0AE"
                  width="10rem"
                  isLoading={isSubmitting}
                  disabled={!isValid}
                >
                  {submitButtonLabel}
                </Button>
              </Flex>
            </Container>
          </Form>
        )
      }}
    </Formik>
  )
}
